import {
  AverageCompanyReviewScoreBreakdown_organization$key,
  UserReviewRating,
} from '#app/__generated__/relay/AverageCompanyReviewScoreBreakdown_organization.graphql.js';
import { Button } from '#app/components/Buttons/Button.js';
import { Link } from '#app/components/Link/index.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useMatchMedia } from '#app/hooks/useMatchMedia.js';
import { parseMediaQueryForMatchMedia } from '#app/utilities/parseMediaQueryForMatchMedia.js';
import { graphql, useFragment } from '@contra/relay';
import { getRoute } from '@contra/routes';
import { css, styled, useTheme } from 'styled-components';

export const BlackStarIcon = () => {
  return (
    <svg
      fill="none"
      height="16"
      viewBox="0 0 16 16"
      width="16"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M7.26978 0.374243C7.49838 -0.124747 8.20737 -0.124748 8.43597 0.374242L10.2653 4.36733C10.3587 4.57119 10.5518 4.71148 10.7745 4.7373L15.1375 5.24316C15.6827 5.30637 15.9018 5.98066 15.4978 6.35227L12.2655 9.32599C12.1004 9.47781 12.0267 9.70479 12.071 9.92462L12.9381 14.2303C13.0464 14.7684 12.4729 15.1851 11.9946 14.9158L8.16759 12.7606C7.97221 12.6506 7.73354 12.6506 7.53816 12.7606L3.71113 14.9158C3.23289 15.1851 2.6593 14.7684 2.76766 14.2303L3.63479 9.92462C3.67906 9.70479 3.60531 9.47781 3.44028 9.32599L0.20792 6.35227C-0.196007 5.98067 0.023082 5.30637 0.568291 5.24316L4.93123 4.7373C5.15398 4.71148 5.34707 4.57119 5.44046 4.36733L7.26978 0.374243Z"
        fill="#14171F"
      />
    </svg>
  );
};

const ProgressBar = styled.div<{ progress: number }>`
  ${({ progress, theme }) => css`
    background-color: ${theme.colorsV2.gray20};
    border-radius: 10px;
    height: 3px;
    width: 110px;

    &::after {
      background-color: ${theme.colorsV2.blue20};
      border-radius: 10px;
      content: '';
      display: block;
      height: 100%;
      width: ${progress}%;
    }
  `}
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Container = styled.div`
  ${({ theme }) => css`
    border-bottom: 1px solid ${theme.colorsV2.gray30};
    padding: 0 0 24px 0;
  `}
`;

const Content = styled.div`
  display: grid;
  margin-top: 24px;

  grid-template-columns: 1fr;
  grid-template-rows: 1fr 1fr 1fr 1fr;
  gap: 24px;
  row-gap: 24px;

  ${({ theme }) => css`
    ${theme.mediaQueries.sm} {
      grid-template-columns: 1fr 1fr;
      grid-template-rows: 1fr 1fr;
      gap: 48px;
      row-gap: 24px;
    }
  `}
`;

const Item = styled.div`
  display: flex;
  gap: 10px;
  width: 100%;
  justify-content: space-between;
  align-items: center;
  p {
    color: ${({ theme }) => theme.colorsV2.gray70};
    font-weight: 600;
  }
`;

const Right = styled.div`
  display: flex;
  align-items: center;
  justify-content: right;
  gap: 12px;
  width: 100%;
  max-width: fit-content;
`;

const Count = styled(Text)`
  font-size: 18px;
  color: ${({ theme }) => theme.colorsV2.black};
  font-weight: 500;
  line-height: 28px;
  letter-spacing: -0.01em;
  text-align: left;
  display: flex;
  align-items: center;
  gap: 4px;
`;

const formatLabel = (label: UserReviewRating): string => {
  const labelMap = {
    RATING_CLARITY_OF_PROJECT_REQUIREMENTS: 'Clarity of requirements',
    RATING_COMMUNICATION: 'Communication',
    RATING_PROFESSIONALISM: 'Professionalism',
    RATING_RESPONSIVENESS: 'Responsiveness',
  };

  return labelMap[label] ?? '';
};

const ALLOWED_RATINGS = [
  'RATING_CLARITY_OF_PROJECT_REQUIREMENTS',
  'RATING_COMMUNICATION',
  'RATING_PROFESSIONALISM',
  'RATING_RESPONSIVENESS',
];

export const AverageCompanyReviewScoreBreakdown = ({
  organizationRef,
  showViewAll,
}: {
  readonly organizationRef: AverageCompanyReviewScoreBreakdown_organization$key;
  readonly showViewAll?: boolean;
}) => {
  const theme = useTheme();
  const isMediumScreen = useMatchMedia(
    parseMediaQueryForMatchMedia(theme.mediaQueries.md),
  );
  const data = useFragment(
    graphql`
      fragment AverageCompanyReviewScoreBreakdown_organization on Organization {
        averageReviewScores {
          averageTotal
          categories {
            id
            nid
            rating
          }
        }

        slug

        userReviews {
          count
        }
      }
    `,
    organizationRef,
  );

  if (!data.averageReviewScores || data.userReviews.count === 0) return null;

  return (
    <Container>
      <Header>
        <Count>
          <BlackStarIcon />
          {data.averageReviewScores.averageTotal} Rating (
          {data.userReviews.count})
        </Count>
        {showViewAll ? (
          <Button
            as={Link}
            to={getRoute('/company/@slug/recommendations', {
              args: { slug: data.slug ?? '' },
            })}
            variant="V2Link"
          >
            View all {isMediumScreen ? 'recommendations' : ''}
          </Button>
        ) : null}
      </Header>

      <Content>
        {data.averageReviewScores?.categories
          ?.filter((category) => ALLOWED_RATINGS.includes(category.nid))
          .map((category) => {
            return (
              <Item
                aria-label={`Rating breakdown ${formatLabel(category.nid)}`}
                key={category.id}
              >
                <Text textStyle="subtitleSmall">
                  {formatLabel(category.nid)}
                </Text>
                <Right>
                  <ProgressBar progress={(category.rating / 5) * 100} />
                  <Text textStyle="subtitleSmall"> {category.rating}</Text>
                </Right>
              </Item>
            );
          })}
      </Content>
    </Container>
  );
};
